import {VillagesData} from '@/actions/villages';
import {BookingExtra, Coupon} from './types';
import {VillageRegion} from '@/types/Villa';
import { Customer } from '@/types/Auth';
import villagesCatalogRequestUrl from '@/utils/village/villages-catalog-request-url';

export interface SendVillageEnquiryRequest {
  villa: string;
  experience: number | null;
  timestampEnd: string;
  timestampStart: string;
  customer: number | null;
  title: string | null;
  firstName: string;
  lastName: string;
  country: number;
  email: string;
  phoneCode: number;
  phone: string;
  adults: number;
  children: number;
  infants: number;
  flexibleDates: boolean;
  note: string;
  availabilityNotification: boolean;
  privacyPolicy: boolean;
  language: string;
}

interface BookVillageResponse {
  id: string;
  error?: string;
}

interface BookVillageRequest {
  adults: number;
  children: number;
  infants: number;
  extras: {villaExtra: number; quantity: number; amount: number}[];
  bedrooms: {
    single?: number;
    double?: number;
    flexible?: number;
    twin?: number;
    triple?: number;
    quadruple?: number;
  };
  villa: number;
  dateStart: string;
  dateEnd: string;
  dateCheckOut: string;
}

interface GetVillageBookingRequest {
  bookingId: string | null;
}

interface GetVillageBookingResponse {
  message?: string;
  data: {
    reservationNumber: string;
    bookingPrice: string;
    balanceMaxPayDate: {
      date: string;
    };
    nights: number;
    realNights: number;
    bedrooms: {
      single: number;
      double: number;
      flexible: number;
      twin: number;
      triple: number;
      quadruple: number;
    };
    checkinTime: {
      date: string;
    };
    checkoutTime: {
      date: string;
    };
    customerNotes: string | null;
    timestampStart: {
      date: string;
    };
    timestampEnd: {
      date: string;
    };
    timestampCheckOut: {
      date: string;
    };
    type: string;
    timestampExpiration: {
      date: string;
    };
    adults: number;
    children: number;
    infants: number;
    extras: BookingExtra[];
    totalPrice: string;
    manualDiscount: string | null;
    coupon: Coupon | null;
    productReview: string | null;
    allowWireTransfer: boolean;
    villa: string;
  };
}

interface EditBookingResponse {
  customerNotes?: string;
  ownerNotes?: string;
  adminNotes?: string;
  localManagerNotes?: string;
  villa: {
    id: number;
  };
  timestampStart: string;
  timestampEnd: string;
  timestampCheckOut: string;
  type: string;
  timestampExpiration: string;
  adults: number;
  children: number;
  infants: number;
  totalPrice: number;
  totalOwnerPrice: number;
  villaPrice: number;
  code?: string;
}

interface EditBookingRequest {
  id: number;
  data: {
    adults: number;
    children: number;
    infants: number;
    bedrooms: {
      single?: number;
      double?: number;
      flexible?: number;
      twin?: number;
      triple?: number;
      quadruple?: number;
    };
    extras: {
      villaExtra: number;
      quantity: number;
      isOwnerManaged: boolean;
    }[];
  };
}

interface ExtendVillageBookingResponse {
  timestampExpiration: {
    date: string;
    timezone_type: number;
    timezone: string;
  };
}

interface ApplyCouponToBookingParams {
  code: string;
  bookingId: number;
}

interface ApplyCouponToBookingResponse {
  adminNotes: string | null;
  adults: number;
  carRentalLink: string | null;
  children: number;
  code: string | null;
  coupon: Coupon
  customer: Customer | null;
  customerNotes: string | null;
  infants: number;
  reservationNumber: string;
  timestampStart: string;
  timestampEnd: string;
  timestampCheckOut: string;
  timestampExpiration: string;
  totalOwnerPrice: number;
  totalPrice: number;
  type: string;
  villaPrice: number;
  message?: string;
}

export async function fetchVillages(params: {
  offset?: number;
  currency?: string;
  searchParams?: {[key: string]: string | string[] | undefined};
  catalogId?: string;
  region?: VillageRegion;
}): Promise<VillagesData> {

  const url = villagesCatalogRequestUrl({...params})

  const response = await fetch(url, {
    next: {revalidate: 3600},
  });

  const data: VillagesData = await response.json();

  const villagesWithLocation = data.data.filter(
    (village) => !!village.location,
  );

  return {
    ...data,
    data: villagesWithLocation,
  };
}

export async function editVillageBooking({id, data}: EditBookingRequest) {
  const response = await fetch(process.env.API_URL + `/booking/${id}/edit`, {
    cache: 'no-store',
    method: 'PATCH',
    body: JSON.stringify(data),
  });

  const responseData: EditBookingResponse = await response.json();

  return responseData;
}

export async function createVillageBooking(
  bookingData: BookVillageRequest,
): Promise<BookVillageResponse> {
  const response = await fetch(process.env.API_URL + `/booking`, {
    cache: 'no-store',
    method: 'POST',
    body: JSON.stringify(bookingData),
  });

  const data: {id: string; error?: string} = await response.json();

  return data;
}

export async function getVillageBooking({
  bookingId,
}: GetVillageBookingRequest): Promise<GetVillageBookingResponse> {
  const response = await fetch(process.env.API_URL + `/booking/${bookingId}`, {
    cache: 'no-store',
  });

  const data: GetVillageBookingResponse = await response.json();

  return data;
}

export async function extendVillageBooking(
  bookingId: string,
): Promise<ExtendVillageBookingResponse> {
  const response = await fetch(
    process.env.API_URL + `/booking/extend-time/${bookingId}`,
    {cache: 'no-store'},
  );

  const data: ExtendVillageBookingResponse = await response.json();

  return data;
}

export async function sendVillageEnquiry(params: SendVillageEnquiryRequest) {
  const response = await fetch(process.env.API_URL + `/villas/enquiry`, {
    method: 'POST',
    body: JSON.stringify(params),
  });

  const data: {data: boolean} = await response.json();

  return data;
}

export async function applyCouponToBooking({
  code,
  bookingId,
}: ApplyCouponToBookingParams) {

  const response = await fetch(process.env.API_URL + `/booking/apply-coupon/${bookingId}`, {
    method: 'PATCH',
    body: JSON.stringify({code}),
  });

  const data: ApplyCouponToBookingResponse = await response.json()

  return data;
}